import { type FormEvent, useCallback, useEffect, useState } from "react";

import { type Activity, type ActivityDraft, adminApi } from "@/lib/adminApi";
import { ActionRow, Field, PanelSection, StatusBanner } from "@/components/ui";
import { useSaveState } from "@/lib/useSaveState";

const EMPTY: ActivityDraft = {
  title: "",
  date: "",
  start_time: "17:00",
  detail: "",
  location_note: "",
};

function formatWhen(activity: Activity): string {
  const when = new Date(`${activity.date}T${activity.start_time}`);
  return when.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function isPast(activity: Activity): boolean {
  return activity.date < new Date().toISOString().slice(0, 10);
}

interface ActivitiesPanelProps {
  readonly onUnauthenticated: () => void;
}

export function ActivitiesPanel({ onUnauthenticated }: ActivitiesPanelProps) {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [draft, setDraft] = useState<ActivityDraft>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const save = useSaveState(onUnauthenticated);

  const reload = useCallback(async () => {
    try {
      setActivities((await adminApi.activities()).activities);
      setLoadError(null);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "Could not load events.");
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const resetForm = () => {
    setDraft(EMPTY);
    setEditingId(null);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const result = await save.run(
      () => (editingId ? adminApi.updateActivity(editingId, draft) : adminApi.addActivity(draft)),
      (activity) => `Saved "${activity.title}".`,
    );
    if (result) {
      resetForm();
      await reload();
    }
  };

  const startEditing = (activity: Activity) => {
    const { id: _id, ...rest } = activity;
    setDraft(rest);
    setEditingId(activity.id);
    save.reset();
  };

  const remove = async (activity: Activity) => {
    if (!window.confirm(`Delete "${activity.title}"? This cannot be undone.`)) return;
    const done = await save.run(
      () => adminApi.deleteActivity(activity.id),
      () => `Deleted "${activity.title}".`,
    );
    if (done) {
      if (editingId === activity.id) resetForm();
      await reload();
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <PanelSection
        title={editingId ? "Edit event" : "Add an event"}
        description="Tournaments, socials, one-off sessions. The weekly meeting is set on the Club tab."
      >
        <form onSubmit={submit}>
          <div className="grid gap-4 sm:grid-cols-[1fr_10rem_8rem]">
            <Field label="Title" htmlFor="activity-title">
              <input
                id="activity-title"
                className="input"
                required
                maxLength={120}
                placeholder="Fall Tournament"
                value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              />
            </Field>
            <Field label="Date" htmlFor="activity-date">
              <input
                id="activity-date"
                className="input input--mono"
                type="date"
                required
                value={draft.date}
                onChange={(event) => setDraft({ ...draft, date: event.target.value })}
              />
            </Field>
            <Field label="Start" htmlFor="activity-time">
              <input
                id="activity-time"
                className="input input--mono"
                type="time"
                required
                value={draft.start_time}
                onChange={(event) => setDraft({ ...draft, start_time: event.target.value })}
              />
            </Field>
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Details" htmlFor="activity-detail" hint="One or two lines.">
              <textarea
                id="activity-detail"
                className="input"
                rows={2}
                maxLength={500}
                value={draft.detail}
                onChange={(event) => setDraft({ ...draft, detail: event.target.value })}
              />
            </Field>
            <Field
              label="Where"
              htmlFor="activity-location"
              hint="Leave blank to use the usual meeting note."
            >
              <textarea
                id="activity-location"
                className="input"
                rows={2}
                maxLength={200}
                value={draft.location_note}
                onChange={(event) => setDraft({ ...draft, location_note: event.target.value })}
              />
            </Field>
          </div>

          <ActionRow>
            {editingId ? (
              <button type="button" className="btn btn--ghost" onClick={resetForm}>
                Cancel
              </button>
            ) : null}
            <button type="submit" className="btn" disabled={save.saving}>
              {save.saving ? "Saving…" : editingId ? "Save changes" : "Add event"}
            </button>
          </ActionRow>
        </form>
      </PanelSection>

      <StatusBanner tone={save.tone} message={save.message} />
      <StatusBanner tone="error" message={loadError} />

      <PanelSection
        title={`Scheduled events (${activities.length})`}
        description={
          activities.length === 0
            ? "Nothing scheduled. The weekly meeting still shows on the site."
            : undefined
        }
      >
        <ul className="flex flex-col gap-3">
          {activities.map((activity) => (
            <li
              key={activity.id}
              className={`flex flex-wrap items-start gap-x-4 gap-y-2 border-2 border-ink
                px-4 py-3 ${isPast(activity) ? "opacity-50" : "bg-field/40"}`}
            >
              <div className="min-w-0 flex-1">
                <p className="display text-lg leading-tight">{activity.title}</p>
                <p className="mt-1 font-mono text-[0.8125rem]">
                  {formatWhen(activity)}
                  {isPast(activity) ? " · past" : ""}
                </p>
                {activity.detail ? (
                  <p className="mt-1 text-[0.875rem] leading-snug opacity-70">
                    {activity.detail}
                  </p>
                ) : null}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn btn--ghost px-3 py-1.5 text-xs"
                  onClick={() => startEditing(activity)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn px-3 py-1.5 text-xs"
                  // White on this red clears AA; the marigold the button would
                  // otherwise inherit does not.
                  style={{
                    background: "var(--color-piece-z)",
                    borderColor: "var(--color-ink)",
                    color: "#fff",
                  }}
                  onClick={() => void remove(activity)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      </PanelSection>
    </div>
  );
}
